<template>
    <div class="rounded border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
        <!-- Row 1: State, City, Type, Min SF, Max SF -->
        <div class="flex flex-wrap items-end gap-4">
            <!-- State (narrow) -->
            <div class="w-24">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">State</label>
                <select v-model="stateCode"
                        class="w-full text-sm rounded border-gray-300 dark:bg-slate-900 dark:border-slate-600 dark:text-gray-100">
                    <option value="">—</option>
                    <option v-for="s in states" :key="s.code" :value="s.code">{{ s.code }}</option>
                </select>
            </div>

            <!-- City (max ~50 chars wide; dropdown of major cities for the selected state) -->
            <div class="w-72">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    City
                    <span v-if="!stateCode" class="text-gray-400 dark:text-gray-500 font-normal">(pick a state first)</span>
                </label>
                <select v-if="cityOptions.length"
                        v-model="cityValue"
                        :disabled="!stateCode"
                        class="w-full text-sm rounded border-gray-300 dark:bg-slate-900 dark:border-slate-600 dark:text-gray-100 disabled:opacity-50">
                    <option value="">—</option>
                    <option v-for="c in cityOptions" :key="c" :value="c">{{ c }}</option>
                    <option value="__custom__">Other…</option>
                </select>
                <input v-else
                       type="text"
                       v-model="cityValue"
                       :disabled="!stateCode"
                       :placeholder="stateCode ? 'Enter city' : ''"
                       class="w-full text-sm rounded border-gray-300 dark:bg-slate-900 dark:border-slate-600 dark:text-gray-100 disabled:opacity-50" />
                <input v-if="cityValue === '__custom__'"
                       type="text"
                       v-model="customCity"
                       placeholder="Enter custom city"
                       class="mt-2 w-full text-sm rounded border-gray-300 dark:bg-slate-900 dark:border-slate-600 dark:text-gray-100" />
            </div>

            <!-- Type -->
            <div class="w-32">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                <select v-model="propertyType"
                        class="w-full text-sm rounded border-gray-300 dark:bg-slate-900 dark:border-slate-600 dark:text-gray-100">
                    <option value="office">Office</option>
                    <option value="industrial">Industrial</option>
                    <option value="retail">Retail</option>
                    <option value="land">Land</option>
                </select>
            </div>

            <!-- Min SF -->
            <div class="w-28">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Min SF</label>
                <input v-model.number="minSf" type="number" min="0" step="500" placeholder="0"
                       class="w-full text-sm rounded border-gray-300 dark:bg-slate-900 dark:border-slate-600 dark:text-gray-100" />
            </div>

            <!-- Max SF -->
            <div class="w-28">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Max SF</label>
                <input v-model.number="maxSf" type="number" min="0" step="500" placeholder="—"
                       class="w-full text-sm rounded border-gray-300 dark:bg-slate-900 dark:border-slate-600 dark:text-gray-100" />
            </div>
        </div>

        <div class="mt-4">
            <brokerage-selector></brokerage-selector>
        </div>

        <div class="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <label class="flex items-center gap-1 text-sm text-gray-700 dark:text-gray-200">
                <input type="checkbox" v-model="includeFirecrawlOpen" />
                Also search Firecrawl open web (column B)
            </label>

            <button type="button"
                    :disabled="!canSearch"
                    @click="runSearch"
                    class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded
                           bg-blue-600 hover:bg-blue-700 text-white
                           disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed">
                <svg v-if="isDispatching" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" class="opacity-25"></circle>
                    <path d="M4 12a8 8 0 018-8" stroke="currentColor" stroke-width="3" class="opacity-75"></path>
                </svg>
                {{ isDispatching ? 'Dispatching…' : 'Search' }}
            </button>
        </div>
    </div>
</template>

<script>
import { US_STATES, citiesForState } from '../../utils/usStates';

export default {
    name: 'SearchForm',
    data() {
        return {
            states: US_STATES,
            customCity: '',
        };
    },
    computed: {
        criteria() { return this.$store.state.listingSearch.criteria; },
        isDispatching() { return this.$store.state.listingSearch.isDispatching; },

        propertyType: {
            get() { return this.criteria.property_type; },
            set(v) { this.$store.commit('SET_LISTING_CRITERIA', { property_type: v }); },
        },
        stateCode: {
            get() { return this.criteria.state; },
            set(v) {
                this.$store.commit('SET_LISTING_CRITERIA', { state: (v || '').toUpperCase() });
                // Clear the city when state changes — the city list now belongs
                // to a different state, and a stale value would silently
                // submit a mismatched city/state pair.
                if (this.criteria.city && !this.cityOptionsFor(v).includes(this.criteria.city)) {
                    this.$store.commit('SET_LISTING_CRITERIA', { city: '' });
                    this.customCity = '';
                }
            },
        },
        minSf: {
            get() { return this.criteria.min_sf; },
            set(v) { this.$store.commit('SET_LISTING_CRITERIA', { min_sf: v ?? null }); },
        },
        maxSf: {
            get() { return this.criteria.max_sf; },
            set(v) { this.$store.commit('SET_LISTING_CRITERIA', { max_sf: v ?? null }); },
        },
        includeFirecrawlOpen: {
            get() { return this.criteria.include_firecrawl_open; },
            set(v) { this.$store.commit('SET_LISTING_CRITERIA', { include_firecrawl_open: v }); },
        },

        cityOptions() {
            return citiesForState(this.stateCode);
        },

        // Bound to the city select; "__custom__" sentinel reveals the freeform input.
        cityValue: {
            get() {
                if (!this.criteria.city) return '';
                if (this.cityOptions.includes(this.criteria.city)) return this.criteria.city;
                return '__custom__';
            },
            set(v) {
                if (v === '__custom__') {
                    // Keep store empty until user types a custom city; runSearch
                    // will pick it up from customCity.
                    this.$store.commit('SET_LISTING_CRITERIA', { city: '' });
                } else {
                    this.customCity = '';
                    this.$store.commit('SET_LISTING_CRITERIA', { city: v });
                }
            },
        },

        canSearch() {
            const cityResolved = this.cityValue === '__custom__' ? this.customCity : this.criteria.city;
            return !this.isDispatching
                && this.propertyType
                && this.stateCode
                && cityResolved
                && (this.criteria.brokerage_ids.length > 0 || this.includeFirecrawlOpen);
        },
    },
    watch: {
        customCity(v) {
            // Keep the store in sync as the user types their custom city.
            if (this.cityValue === '__custom__') {
                this.$store.commit('SET_LISTING_CRITERIA', { city: v });
            }
        },
    },
    methods: {
        cityOptionsFor(stateCode) {
            return citiesForState(stateCode);
        },
        runSearch() {
            this.$store.dispatch('runListingSearch');
        },
    },
};
</script>
